/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import React from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Theme } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import SignatureCanvas from 'react-signature-canvas';
import { useStore } from '../../../hooks';
import { TitleComp } from 'libs/feature-borrowers/src/caf/components/titleComp';
import { formatDate } from 'libs/ui/src/lib/components/borrowerBaseballCard/utils';
import { useBorrowerPortalMediaQueries } from 'libs/feature-app-core/src/hooks/useBorrowerPortalMediaQueries';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    sigCanvas: {
      width: '100%',
      height: 200,
      background: '#f7f7f7',
      border: '1px solid #aaa',
      borderRadius: 16,
    },
    button: {
      width: '160px',
      marginBottom: '50px',
    },
  })
);

const DEFAULT_AMC_NAME = 'the appraisal management company';
const TAMARISK_VENDOR_NAME = 'Tamarisk';

const AMC_LEGAL_NAMES: Record<string, string> = {
  tamarisk: 'Tamarisk Appraisals LLC',
  'internal valuation': 'Loan Funder LLC',
};

const isTamariskVendor = (vendorName?: string) =>
  vendorName?.trim().toLowerCase() === TAMARISK_VENDOR_NAME.toLowerCase();

const resolveAmcName = (vendorName?: string) => {
  const trimmedVendorName = vendorName?.trim();

  if (!trimmedVendorName) {
    return DEFAULT_AMC_NAME;
  }

  return AMC_LEGAL_NAMES[trimmedVendorName.toLowerCase()] ?? trimmedVendorName;
};

interface Props {
  onClose: () => void;
  open: boolean;
  successPage: boolean;
}

const ValuationDocumentSignModal = observer((props: Props) => {
  const { valuationPaymentStore } = useStore();
  const { valuationInfo } = valuationPaymentStore;
  const { onClose } = props;
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { isLargeScreen } = useBorrowerPortalMediaQueries();

  const sigCanvas = React.useRef<SignatureCanvas>(null);
  const [signed, setSigned] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);
  const propertyAddresses = valuationPaymentStore.getPropertyAddresses();

  const generateDocument = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      return;
    }

    const signatureDataURL = sigCanvas.current.toDataURL('image/png');

    const wasGenerated = await valuationPaymentStore.generateDocument(signatureDataURL);

    if (wasGenerated) {
      onClose();
    }
  };

const getAcknowledgment = (valuationInfo: any, propertyAddresses: string) => {
  const amcName = resolveAmcName(valuationInfo?.vendorName);

  return (
  <ul style={{ marginLeft: '16px', listStyleType: 'none' }}>
    <li>
      Purpose:{' '}
      {isTamariskVendor(valuationInfo?.vendorName)
        ? 'Appraisal'
        : 'Valuation Service'}
    </li>
    <li style={{ marginBottom: '12px' }}>
      {valuationInfo?.properties?.length > 1 ? 'Property Addresses' : 'Property Address'}:{' '}
      {propertyAddresses || 'N/A'}
    </li>
    <li style={{ marginBottom: '12px' }}>
      This document authorizes {amcName} to process a one-time charge to the credit card provided for the appraisal services requested by the undersigned. This payment authorization is for the amount disclosed at the time the appraisal order is placed and is valid for one-time use only.
    </li>
    <li style={{ marginBottom: '12px' }}>I certify and acknowledge that:</li>
    <li>
      <ul style={{ marginLeft: '16px', listStyleType: 'none' }}>
        <li style={{ marginBottom: '12px' }}>
          1. I am an authorized user of the credit card provided and authorize {amcName} to charge my credit card for the requested appraisal services.
        </li>
        <li style={{ marginBottom: '12px' }}>
          2. If signing on behalf of a borrower, lender, company, trust, or other entity, I represent that I have the authority to execute this authorization on its behalf.
        </li>
        <li style={{ marginBottom: '12px' }}>
          3. I understand that the appraisal fee is charged for professional appraisal services rendered and is not contingent upon or a guarantee of any particular appraised value, loan approval, loan terms, refinancing, purchase, sale, or any other transaction outcome. I further understand that neither {amcName} nor the appraiser can guarantee any specific appraised value or outcome.
        </li>
        <li style={{ marginBottom: '12px' }}>
          4. I understand that a minimum $50 cancellation fee will be charged if the appraisal order is canceled after it has been accepted and prior to the appraisal inspection. If the appraisal inspection has occurred, a minimum inspection fee of $150 will be charged, with the actual fee varying based on the work performed. If the appraisal has been partially completed, a fee will be charged based on the extent of the work performed and may be up to the full appraisal fee. If the appraisal has been completed, the full appraisal fee will be charged.
        </li>
        <li style={{ marginBottom: '12px' }}>
          5. I certify that the information I have provided is accurate and complete, including the billing information associated with the credit card used for this transaction. I acknowledge responsibility for any errors resulting from incorrect or inaccurate information provided.
        </li>
      </ul>
    </li>
    <li style={{ marginBottom: '12px' }}>
      By signing below, I acknowledge that I have read and agree to this Payment Authorization.
    </li>
  </ul>
  );
};

  return (
    <Dialog
      disableEscapeKeyDown
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      open={props.open}
      scroll="paper"
      onClose={onClose}
      aria-labelledby="valuation-document-dialog"
    >
      <DialogTitle disableTypography>
        <Typography variant="h4">Valuation Payment Authorization</Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Box m={4}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <Grid xs={12}>
              <Box mb={1}>
                <TitleComp title="Acknowledgments" />
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Grid item xs={12}>
              <Box mb={3}>
                <Paper
                  variant="outlined"
                  style={{
                    padding: 24,
                    overflowY: 'auto',
                    borderColor: '#ccc',
                    maxHeight: isLargeScreen ? 300 : 150,
                  }}
                >
                  <Typography
                    variant="body1"
                    style={{
                      fontStyle: 'italic',
                    }}
                  >
                  {getAcknowledgment(valuationInfo, propertyAddresses)}
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  color="primary"
                />
              }
              label="I have read and agree to the terms above"
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <Grid xs={12}>
              <Box my={3}>
                <TitleComp
                  title={
                    <span>
                      Signature
                      <Box color="red" component={'span'} ml={0.5}>
                        *
                      </Box>
                    </span>
                  }
                />
              </Box>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <SignatureCanvas
                  penColor="green"
                  canvasProps={{ className: classes.sigCanvas }}
                  ref={sigCanvas}
                  onEnd={() => setSigned(true)}
                />
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  width="100%"
                >
                  <Button
                    size="small"
                    onClick={() => {
                      sigCanvas.current?.clear();
                      setSigned(false);
                    }}
                  >
                    Clear
                  </Button>
                  <Box color={'blue'}>{formatDate(new Date())}</Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => generateDocument()}
                disabled={!agreed || !signed || sigCanvas.current?.isEmpty()}
              >
                SUBMIT
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
});

export default ValuationDocumentSignModal;
